import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import emailjs from "@emailjs/browser";
import DOMPurify from "dompurify";

// You need to define these validators
import { isValidEmail, isValidUSPhone } from "./util";

export default function SanitizedForm() {
    const formRef = useRef<HTMLFormElement>(null);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    useEffect(() => {
        const alreadySubmitted =
            sessionStorage.getItem("formSubmitted") === "true";
        if (alreadySubmitted) setSubmitted(true);
    }, []);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        const sanitizedValue = DOMPurify.sanitize(value, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: [],
        });
        setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: typeof errors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required.";
        if (!formData.email.trim()) {
            newErrors.email = "Email is required.";
        } else if (!isValidEmail(formData.email)) {
            newErrors.email = "Enter a valid email address.";
        }
        if (!formData.phone.trim()) newErrors.phone = "Phone is required.";
        else if (!isValidUSPhone(formData.phone)) {
            newErrors.phone = "Enter a valid phone number.";
        }
        if (!formData.message.trim())
            newErrors.message = "Message is required.";

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        if (!formRef.current) return;

        setLoading(true);
        try {
            const result = await emailjs.sendForm(
                "service_40z168q", // 🔁 replace with your EmailJS service ID
                "template_12psqep", // 🔁 replace with your EmailJS template ID
                formRef.current,
                "nR1Wm2CJCbbl7dHM8" // 🔁 replace with your EmailJS public key
            );

            sessionStorage.setItem("formSubmitted", "true");
            setSubmitted(true);

            setSnackbar({
                open: true,
                message: "Message sent successfully!",
                severity: "success",
            });
            setFormData({ name: "", email: "", phone: "", message: "" });
            setErrors({});
            formRef.current.reset();
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: "Failed to send message. Please try again later.",
                severity: "error",
            });
            console.error("EmailJS error:", error.text || error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: "auto", mt: 2 }}>
            {!submitted ? (
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                    ref={formRef}
                >
                    <Grid
                        container
                        spacing={2}
                        sx={{ flexDirection: "column" }}
                    >
                        <Grid>
                            <TextField
                                fullWidth
                                required
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid>
                            <TextField
                                fullWidth
                                required
                                type="email"
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                error={!!errors.email}
                                helperText={errors.email}
                            />
                        </Grid>
                        <Grid>
                            <TextField
                                fullWidth
                                required
                                type="tel"
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                error={!!errors.phone}
                                helperText={errors.phone}
                            />
                        </Grid>
                        <Grid>
                            <TextField
                                fullWidth
                                required
                                label="Message"
                                name="message"
                                multiline
                                rows={4}
                                value={formData.message}
                                onChange={handleChange}
                                error={!!errors.message}
                                helperText={errors.message}
                            />
                        </Grid>
                        <Grid
                            sx={{ display: "flex", justifyContent: "center" }}
                        >
                            <Button
                                variant="contained"
                                type="submit"
                                sx={{ width: "50%", position: "relative" }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress
                                        size={24}
                                        sx={{ color: "white" }}
                                    />
                                ) : (
                                    "Submit"
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <Alert severity="success" sx={{ textAlign: "center" }}>
                    Thank you! Your message has been sent.
                </Alert>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() =>
                    setSnackbar((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}
